import React, { useState } from 'react';

/**
 * Get status color
 */
function getStatusColor(status) {
  switch (status?.toLowerCase()) {
    case 'fresh':
    case 'strong':
      return 'var(--success)';
    case 'fading':
      return 'var(--warning)';
    case 'weak':
    case 'forgotten':
      return 'var(--danger)';
    default:
      return 'var(--text-tertiary)';
  }
}

/**
 * Format next review date
 */
function formatNextReview(dateString) {
  if (!dateString) return 'Not scheduled';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
  if (diffDays === 0) return 'Due today';
  if (diffDays === 1) return 'Due tomorrow';
  return `Due in ${diffDays} days`;
}

/**
 * Grade button component
 */
function GradeButton({ grade, label, color, onClick, disabled, isLoading }) {
  return (
    <button
      type="button"
      onClick={() => onClick(grade)}
      disabled={disabled || isLoading}
      style={{
        flex: 1,
        padding: 'var(--space-3) var(--space-2)',
        borderRadius: 'var(--radius-control)',
        border: 'none',
        cursor: disabled || isLoading ? 'not-allowed' : 'pointer',
        transition: 'all 0.15s ease',
        fontSize: 12,
        fontWeight: 600,
        background: color,
        color: 'var(--text-inverted)',
        opacity: disabled || isLoading ? 0.6 : 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-1)',
      }}
    >
      {isLoading ? (
        <div style={{
          width: 14,
          height: 14,
          border: '2px solid var(--border-strong)',
          borderTopColor: 'var(--text-inverted)',
          borderRadius: '50%',
          animation: 'graspd-spin 0.6s linear infinite',
        }} />
      ) : (
        label
      )}
    </button>
  );
}

/**
 * ReviewCard - Shows a single review item with grade buttons
 */
export function ReviewCard({
  conceptId,
  courseId,
  conceptName,
  retrievability,
  status,
  priority,
  lastReviewAt,
  nextReviewAt,
  onGrade,
  isSubmitting = false,
}) {
  const [activeGrade, setActiveGrade] = useState(null);
  const [showResult, setShowResult] = useState(null);
  const statusColor = getStatusColor(status);
  const retrievabilityPct = Math.round((retrievability || 0) * 100);

  const handleGrade = async (grade) => {
    setActiveGrade(grade);
    try {
      const result = await onGrade(conceptId, grade);
      setShowResult(result);
    } catch (err) {
      setActiveGrade(null);
    }
  };

  // Show success state after grading
  if (showResult) {
    return (
      <div style={{
        background: 'var(--bg-secondary)',
        border: '1px solid var(--success)',
        borderRadius: 'var(--radius-panel)',
        padding: 'var(--space-4)',
        textAlign: 'center',
        animation: 'fadeIn 0.3s ease',
      }}>
        <div style={{
          fontSize: 24,
          marginBottom: 'var(--space-2)',
        }}>
          ✓
        </div>
        <div style={{
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--text-primary)',
          marginBottom: 'var(--space-1)',
        }}>
          Reviewed!
        </div>
        <div style={{
          fontSize: 12,
          color: 'var(--text-secondary)',
        }}>
          Next review: {showResult.nextReviewAt ? new Date(showResult.nextReviewAt).toLocaleDateString() : 'Soon'}
        </div>
        <style>{`@keyframes fadeIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--bg-secondary)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 'var(--radius-panel)',
      padding: 'var(--space-4)',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-3)' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Concept Name */}
          <div style={{
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--text-primary)',
            marginBottom: 'var(--space-1)',
          }}>
            {conceptName || 'Unnamed Concept'}
          </div>
          {/* Status Badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
          }}>
            <span style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: statusColor,
            }} />
            <span style={{
              fontSize: 12,
              color: statusColor,
              fontWeight: 500,
            }}>
              {status || 'Unknown'} ({retrievabilityPct}% recall)
            </span>
          </div>
        </div>

        {/* Priority indicator */}
        {priority > 0.7 && (
          <span style={{
            fontSize: 10,
            fontWeight: 600,
            padding: 'var(--space-1) var(--space-2)',
            borderRadius: 'var(--radius-control)',
            background: 'var(--danger)',
            color: 'var(--text-inverted)',
          }}>
            High Priority
          </span>
        )}
      </div>

      {/* Retrievability bar */}
      <div style={{ marginBottom: 'var(--space-3)' }}>
        <div style={{
          height: 4,
          borderRadius: 'var(--radius-full)',
          background: 'var(--bg-tertiary)',
          overflow: 'hidden',
        }}>
          <div style={{
            height: '100%',
            width: `${retrievabilityPct}%`,
            background: statusColor,
            borderRadius: 'var(--radius-full)',
            transition: 'width 0.3s ease',
          }} />
        </div>
      </div>

      {/* Due date */}
      <div style={{
        fontSize: 11,
        color: 'var(--text-tertiary)',
        marginBottom: 'var(--space-3)',
      }}>
        {formatNextReview(nextReviewAt)}
      </div>

      {/* Grade buttons */}
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <GradeButton
          grade="AGAIN"
          label="Again"
          color="var(--danger)"
          onClick={handleGrade}
          disabled={isSubmitting}
          isLoading={activeGrade === 'AGAIN' && isSubmitting}
        />
        <GradeButton
          grade="HARD"
          label="Hard"
          color="var(--warning)"
          onClick={handleGrade}
          disabled={isSubmitting}
          isLoading={activeGrade === 'HARD' && isSubmitting}
        />
        <GradeButton
          grade="GOOD"
          label="Good"
          color="var(--accent-primary)"
          onClick={handleGrade}
          disabled={isSubmitting}
          isLoading={activeGrade === 'GOOD' && isSubmitting}
        />
        <GradeButton
          grade="EASY"
          label="Easy"
          color="var(--success)"
          onClick={handleGrade}
          disabled={isSubmitting}
          isLoading={activeGrade === 'EASY' && isSubmitting}
        />
      </div>

    </div>
  );
}
